<template>
  <div class="d-flex justify-center">
    <v-card elevation="2">
      <v-card-text>
        <div class="d-flex align-center">
          <SelectionStat
            :searched-length="searchFilteredPoems.length"
            :filtered-length="filteredPoems.length"
            :total-length="poemsTotal"
            :stat="stat"
          />
          <v-spacer></v-spacer>
          <v-tooltip top>
            <template v-slot:activator="{ on }">
              <v-btn
                v-on="on"
                icon
                class="ml-2"
                @click="downloadTable"
                :loading="downloading"
                :disabled="downloading"
              >
                <v-icon>{{ icon.download }}</v-icon>
              </v-btn>
            </template>
            <span>{{ $t("table.downloadTable") }}</span>
          </v-tooltip>
          <InstructionIcon v-model="showInstruction" />
          <InstructionList
            :show="showInstruction"
            :list="$t('table.instructions')"
          />
        </div>
        <div class="d-flex align-end pb-4">
          <div style="width: 360px" class="d-flex align-end">
            <v-icon class="mr-2">{{ icon.search }}</v-icon>
            <v-text-field
              v-model="search"
              :label="$t('table.search')"
              single-line
              hide-details
              clearable
            >
            </v-text-field>
          </div>
          <div class="ml-2">
            <SearchColumnsControl
              v-model="selectedSearchColumns"
              :items="searchColumns"
            />
          </div>
          <v-spacer></v-spacer>
          <ResetButton
            :text="$t('app.resetFilters')"
            :show="highlightFreezed"
            @reset="resetHighlight"
          />
        </div>
        <div class="poem-table">
          <v-data-table
            :headers="headers"
            :items="sortedPoems"
            item-key="Nr"
            multi-sort
            :sort-by.sync="sortBy"
            :sort-desc.sync="sortDesc"
            :items-per-page.sync="itemsPerPage"
            :page.sync="page"
            :locale="this.$i18n.locale"
          >
            <template v-slot:item="{ item }">
              <tr
                :class="{
                  'v-data-table__selected':
                    pickedPoem && item.Nr === pickedPoem.Nr
                }"
              >
                <td>
                  <span v-html="mark(item.Titel, 'Titel')"></span>
                </td>
                <td>
                  <span
                    v-html="mark(item.Gedichtanfänge, 'Gedichtanfänge')"
                  ></span>
                </td>
                <td>
                  <v-icon
                    :class="{
                      'mark--icon': fullTextIconMarked(item['Gedicht (Text)'])
                    }"
                    @mouseenter="onMouseEnterFulltext(item, $event)"
                    @mouseleave="onMouseLeaveFulltext"
                    >{{ icon.fullText }}</v-icon
                  >
                </td>
                <td>
                  <span v-html="mark(item.Jahr, 'Jahr')"></span>
                </td>
                <td>
                  <span
                    v-for="(theme, i) in item.Thema"
                    :key="i"
                    :class="{
                      'font-weight-bold': highlightedThemes.includes(theme)
                    }"
                    :style="{
                      color: color(theme)
                    }"
                    @click.stop="onThemeClick(theme)"
                    ><span v-html="mark(theme, 'Thema')"></span
                    >{{ i === item.Thema.length - 1 ? "" : ", " }}</span
                  >
                </td>
                <td>
                  <span
                    v-html="mark(item.Lebensabschnitt, 'Lebensabschnitt')"
                  ></span>
                </td>
                <td>
                  <span v-html="mark(item.Sentiment, 'Sentiment')"></span>
                </td>
                <td>
                  <span
                    v-for="(mood, i) in item.Stimmung"
                    :key="i"
                    :class="{
                      'font-weight-bold': highlightedMoods.includes(mood)
                    }"
                    ><span v-html="mark(mood, 'Stimmung')"></span
                    >{{ i === item.Stimmung.length - 1 ? "" : ", " }}</span
                  >
                </td>
                <td>
                  <span
                    v-for="(motif, i) in item.Motive"
                    :key="i"
                    :class="{
                      'font-weight-bold': highlightedMotifs.includes(motif)
                    }"
                    ><span v-html="mark(motif, 'Motive')"></span
                    >{{ i === item.Motive.length - 1 ? "" : ", " }}</span
                  >
                </td>
              </tr>
            </template>
          </v-data-table>
        </div>
      </v-card-text>
    </v-card>
    <Tooltip
      v-if="hoveredPoem"
      :show="showTooltip"
      :position="pointerPosition"
      @mouseenter="onMouseEnterFulltext(hoveredPoem)"
      @mouseleave="onMouseLeaveFulltext()"
    >
      <div class="black--text">
        <div class="d-flex">
          <p>
            <span
              class="font-weight-bold"
              v-html="mark(hoveredPoem.Titel, 'Titel')"
            ></span>
            <span> {{ hoveredPoem.Zeit }}</span>
          </p>
          <v-spacer></v-spacer>
          <v-tooltip top>
            <template v-slot:activator="{ on: { mouseenter, mouseleave } }">
              <v-btn
                icon
                class="ml-2"
                @mouseenter="mouseenter"
                @mouseleave="mouseleaveCopyToClipboard(mouseleave)"
                @click="copyToClipboard(hoveredPoem)"
              >
                <v-icon>{{ icon.copy }}</v-icon>
              </v-btn>
            </template>
            <span>{{
              copying ? $t("table.copied") : $t("table.copyPoem")
            }}</span>
          </v-tooltip>
        </div>
        <p
          style="white-space: pre"
          v-html="mark(hoveredPoem['Gedicht (Text)'], 'Gedicht (Text)')"
        ></p>
      </div>
    </Tooltip>
  </div>
</template>

<script>
import { mapState, mapMutations, mapActions, mapGetters } from "vuex";
import {
  mdiMagnify,
  mdiBook,
  mdiContentCopy,
  mdiFileDownloadOutline
} from "@mdi/js";
import ResetButton from "@/components/ResetButton.vue";
import Tooltip from "@/components/Tooltip.vue";
import InstructionList from "@/components/InstructionList.vue";
import InstructionIcon from "@/components/InstructionIcon.vue";
import { sortItems } from "vuetify/es5/util/helpers";
import { unparse } from "papaparse";
import createSearch from "@/utils/create-search";
import SearchColumnsControl from "./SearchColumnsControl.vue";
import SelectionStat from "./SelectionStat.vue";
export default {
  components: {
    ResetButton,
    Tooltip,
    InstructionList,
    InstructionIcon,
    SearchColumnsControl,
    SelectionStat
  },
  data: () => ({
    icon: {
      search: mdiMagnify,
      fullText: mdiBook,
      copy: mdiContentCopy,
      download: mdiFileDownloadOutline
    },
    search: "",
    selectedSearchColumns: [],
    hoveredPoem: null,
    showTooltip: false,
    itemsPerPage: 10,
    page: 1,
    sortDesc: [false],
    sortBy: ["Jahr"],
    showInstruction: false,
    downloading: false,
    copying: false
  }),
  computed: {
    ...mapState([
      "highlightedMoods",
      "highlightedMotifs",
      "highlightedThemes",
      "highlightFreezed",
      "pickedPoem",
      "interactingCategory",
      "interactingCategoryValues",
      "isShowingInstructionIcons",
      "filteredPoems",
      "highlightedPoems"
    ]),
    ...mapGetters([
      "displayHighlightedNotFilteredPoems",
      "color",
      "poemsTotal"
    ]),
    headers() {
      return [
        {
          text: this.$t("data.title"),
          value: "Titel",
          sortable: true,
          filterable: true,
          getContent: d => d.Titel
        },
        {
          text: this.$t("data.poemBeginning"),
          value: "Gedichtanfänge",
          sortable: true,
          filterable: true,
          width: 154,
          getContent: d => d.firstSentence
        },
        {
          text: this.$t("data.text"),
          value: "Gedicht (Text)",
          sortable: false,
          filterable: true,
          getContent: d => d["Gedicht (Text)"]
        },
        {
          text: this.$t("data.year"),
          value: "Jahr",
          sortable: true,
          filterable: true,
          width: 80,
          getContent: d => d.year
        },
        {
          text: this.$t("data.theme"),
          value: "Thema",
          sortable: true,
          filterable: true,
          getContent: d => d.themes
        },
        {
          text: this.$t("data.stageOfLife"),
          value: "Lebensabschnitt",
          sortable: true,
          filterable: true,
          width: 163,
          getContent: d => d.Lebensabschnitt
        },
        {
          text: this.$t("data.sentiment"),
          value: "Sentiment",
          sortable: true,
          filterable: true,
          width: 118,
          getContent: d => d.Sentiment[0].toUpperCase() + d.Sentiment.slice(1)
        },
        {
          text: this.$t("data.mood"),
          value: "Stimmung",
          sortable: true,
          filterable: true,
          getContent: d => d.moods
        },
        {
          text: this.$t("data.motif"),
          value: "Motive",
          sortable: true,
          filterable: true,
          getContent: d => d.motifs
        }
      ];
    },
    searchColumns() {
      return this.headers
        .filter(d => d.filterable)
        .map(d => ({
          text: d.text,
          value: d.value
        }));
    },
    searchableHeaderColumns() {
      if (this.selectedSearchColumns.length > 0) {
        return this.selectedSearchColumns;
      } else {
        return this.headers.filter(d => d.filterable).map(d => d.value);
      }
    },
    poemsWithOnlyHeaderColumns() {
      return (this.displayHighlightedNotFilteredPoems
        ? this.highlightedPoems
        : this.filteredPoems
      ).map(d => {
        const p = {
          Nr: d.Nr
        };
        this.headers.forEach(h => {
          p[h.value] = h.getContent(d);
        });
        return p;
      });
    },
    searchFilteredPoems() {
      if (this.search && this.search !== '"') {
        return this.poemsWithOnlyHeaderColumns.filter(d => {
          return this.searchRegexArr.every(r => {
            return this.searchableHeaderColumns.some(c => {
              return String(d[c]).search(r) !== -1;
            });
          });
        });
      } else {
        return this.poemsWithOnlyHeaderColumns;
      }
    },
    sortedPoems() {
      return sortItems(
        this.searchFilteredPoems.slice(),
        this.sortBy,
        this.sortDesc,
        this.locale
      );
    },
    interactingCategoryValuesByCategory() {
      if (this.interactingCategoryValues.length === 0) return null;
      const categories = this.interactingCategory
        .split("_")
        .slice(1)
        .map(c => c.toLowerCase());
      if (categories[0] === "poem") return null;
      const byCategory = categories.reduce((byCategory, category, i) => {
        byCategory[category] = [
          ...new Set(
            this.interactingCategoryValues.map(v =>
              typeof v === "string" ? v : v[i]
            )
          )
        ];
        return byCategory;
      }, {});
      return byCategory;
    },
    searchRegexArr() {
      return createSearch(this.search);
    },
    stat() {
      const stat = {};
      if (this.interactingCategoryValuesByCategory) {
        ["theme", "mood", "motif"].forEach(category => {
          stat[category] = this.interactingCategoryValuesByCategory[category];
        });
      }
      if (this.search) {
        stat.search = this.search;
      }
      return stat;
    }
  },
  watch: {
    sortedPoems() {
      this.page = 1;
    },
    pickedPoem(value) {
      if (value) this.updateTablePage();
    }
  },
  methods: {
    ...mapMutations(["updateHighlightFreezed"]),
    ...mapActions(["resetHighlight", "updatePickedThemes", "updatePickedPoem"]),
    mark(text, column) {
      let wrapped = String(text);
      if (
        this.search &&
        this.search !== '"' &&
        this.searchableHeaderColumns.includes(column)
      ) {
        this.searchRegexArr.forEach(re => {
          if (wrapped.search(re) !== -1) {
            wrapped = wrapped.replace(re, "<span class='mark'>$&</span>");
          }
        });
        return wrapped;
      } else {
        return wrapped;
      }
    },
    fullTextIconMarked(fullText) {
      return (
        this.search &&
        this.search !== '"' &&
        this.searchRegexArr.some(re => fullText.search(re) !== -1)
      );
    },
    onThemeClick(theme) {
      this.updatePickedThemes(theme);
    },
    onMouseEnterFulltext(poem, event) {
      this.hoveredPoem = poem;
      if (event) {
        this.pointerPosition = [event.clientX, event.clientY];
      }
      this.showTooltip = true;
    },
    onMouseLeaveFulltext() {
      this.showTooltip = false;
    },
    updateTablePage() {
      this.page = Math.max(
        Math.floor(
          this.sortedPoems.findIndex(poem => this.pickedPoem.Nr === poem.Nr) /
            this.itemsPerPage
        ) + 1,
        1
      );
    },
    copyToClipboard(poem) {
      this.copying = true;
      const str = `${poem.Titel} ${poem.Zeit}\n\n${poem["Gedicht (Text)"]}`;
      const el = document.createElement("textarea");
      el.value = str;
      document.body.appendChild(el);
      el.select();
      document.execCommand("copy");
      document.body.removeChild(el);
    },
    mouseleaveCopyToClipboard(mouseleave) {
      mouseleave();
      setTimeout(() => {
        this.copying = false;
      }, 500);
    },
    downloadTable() {
      this.downloading = true;
      const csv = unparse(this.sortedPoems);
      // https://stackoverflow.com/a/24922761
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      if (navigator.msSaveBlob) {
        // IE 10+
        navigator.msSaveBlob(blob, "poems.csv");
      } else {
        var link = document.createElement("a");
        if (link.download !== undefined) {
          // feature detection
          // Browsers that support HTML5 download attribute
          var url = URL.createObjectURL(blob);
          link.setAttribute("href", url);
          link.setAttribute("download", "poems.csv");
          link.style.visibility = "hidden";
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        }
      }
      setTimeout(() => {
        this.downloading = false;
      }, 500);
    }
  }
};
</script>

<style scoped>
>>> .mark {
  background: orange;
}

>>> .mark--icon {
  color: orange;
}
</style>
